import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageViewer } from '@/components/ImageViewer';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function MapDetailPage() {
  const { id } = useParams<{ id: string }>();
  const mapId = Number(id);
  const isValidId = Number.isInteger(mapId) && mapId > 0;

  const {
    data: mapData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['map', mapId],
    queryFn: ({ signal }) => api.getMap(mapId, { signal }),
    enabled: isValidId,
  });

  if (!isValidId) {
    return (
      <div className="text-center py-12">
        <h2 className="text-3xl font-display font-bold text-stone-100 mb-2">Map not found</h2>
        <p className="text-stone-400 mb-6">The map identifier is invalid.</p>
        <Link
          to="/"
          className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  if (isError || !mapData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-3xl font-display font-bold text-stone-100 mb-2">Map not found</h2>
        <p className="text-stone-400 mb-6">
          {error instanceof Error ? error.message : "The map you're looking for doesn't exist."}
        </p>
        <Link
          to="/"
          className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  const { map, images } = mapData;

  return (
    <div className="space-y-8">
      {/* Back button */}
      <Link
        to="/maps"
        className="inline-flex items-center text-gold-400 hover:text-gold-300 font-medium transition-colors"
      >
        ← Back to Browse
      </Link>

      {/* Title */}
      <div>
        <h1 className="text-5xl font-display font-bold text-stone-100 tracking-wide">{map.title}</h1>
      </div>

      {/* Images */}
      <div className="bg-shadow-700 rounded-lg shadow-dark p-6 border border-stone-800">
        <h2 className="text-3xl font-display font-bold text-stone-100 mb-6 tracking-wide">Images</h2>
        {images.length > 0 ? (
          <ImageViewer images={images} />
        ) : (
          <p className="text-stone-400">No images available for this map.</p>
        )}
      </div>

      {/* Description and metadata */}
      {(map.description || map.source_url) && (
        <div className="bg-shadow-700 rounded-lg shadow-dark p-6 border border-stone-800">
          <h2 className="text-3xl font-display font-bold text-stone-100 mb-4 tracking-wide">Details</h2>

          {map.description && (
            <div className="mb-4">
              <p className="text-stone-300 whitespace-pre-wrap">{map.description}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-base text-stone-400 pt-4 border-t border-stone-800">
            <div>
              <span className="font-medium text-stone-300">Created:</span>{' '}
              {formatDate(map.created_at)}
            </div>
            <div>
              <span className="font-medium text-stone-300">Images:</span> {images.length}
            </div>
            <div>
              <a
                href={map.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-400 hover:text-gold-300 font-medium transition-colors"
              >
                View Original Post →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
